import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Container from '../components/common/Container';
import SpacingEffectPreview from '../components/blog/SpacingEffectPreview';
import AIAdaptivePreview from '../components/blog/AIAdaptivePreview';
import PersonalisedLearningPreview from '../components/blog/PersonalisedLearningPreview';
import MasteryLearningPreview from '../components/blog/MasteryLearningPreview';
import ZPDPreview from '../components/blog/ZPDPreview';
import {
  CalendarIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  gradient: string;
  previewType: 'spacing' | 'ai-adaptive' | 'personalised' | 'mastery' | 'zpd';
}

export const blogPosts: BlogPost[] = [
  {
    slug: 'spacing-effect-learning-science',
    title: 'The Spacing Effect: Why Distributed Practice Transforms Learning',
    excerpt: 'Research shows that spacing out learning sessions dramatically improves retention. Discover how the spacing effect is revolutionising educational outcomes in science curricula.',
    category: 'Learning Science',
    readTime: '6 min read',
    date: 'November 2025',
    gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    previewType: 'spacing',
  },
  {
    slug: 'ai-adaptive-learning-platforms',
    title: 'AI-Enabled Adaptive Learning: A Comprehensive Review',
    excerpt: 'Artificial intelligence is reshaping education through personalised learning paths. Explore the latest research on how AI adapts to individual student needs.',
    category: 'EdTech Research',
    readTime: '8 min read',
    date: 'November 2025',
    gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    previewType: 'ai-adaptive',
  },
  {
    slug: 'personalised-learning-student-performance',
    title: 'Personalised Learning: Improving Student Performance and Satisfaction',
    excerpt: 'New studies demonstrate that adaptive learning tools significantly boost both academic outcomes and student engagement in online and face-to-face settings.',
    category: 'Research Insights',
    readTime: '7 min read',
    date: 'October 2025',
    gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    previewType: 'personalised',
  },
  {
    slug: 'mastery-learning-practical-review',
    title: 'Mastery Learning: A Practical Review for Modern Educators',
    excerpt: 'Understanding mastery-based progression and how it ensures students truly grasp concepts before moving forward. Evidence from pharmacy education research.',
    category: 'Teaching Strategies',
    readTime: '5 min read',
    date: 'October 2025',
    gradient: 'from-[var(--color-amber)] to-[var(--color-rose)]',
    previewType: 'mastery',
  },
  {
    slug: 'zone-proximal-development-scaffolding',
    title: 'Zone of Proximal Development: The Science of Scaffolded Learning',
    excerpt: 'How Vygotsky\'s ZPD concept drives modern adaptive learning systems and improves problem-solving skills through intelligent scaffolding.',
    category: 'Learning Theory',
    readTime: '6 min read',
    date: 'September 2025',
    gradient: 'from-[var(--color-rose)] to-[var(--color-violet)]',
    previewType: 'zpd',
  },
];

const renderPreview = (previewType: BlogPost['previewType'], variant: 'card' | 'featured' = 'card') => {
  switch (previewType) {
    case 'spacing':
      return <SpacingEffectPreview variant={variant} />;
    case 'ai-adaptive':
      return <AIAdaptivePreview variant={variant} />;
    case 'personalised':
      return <PersonalisedLearningPreview variant={variant} />;
    case 'mastery':
      return <MasteryLearningPreview variant={variant} />;
    case 'zpd':
      return <ZPDPreview variant={variant} />;
    default:
      return null;
  }
};

const Blog: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[var(--color-electric-blue)] rounded-full filter blur-[128px] opacity-20 float-animation"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[var(--color-violet)] rounded-full filter blur-[128px] opacity-20 float-animation" style={{ animationDelay: '2s' }}></div>
        </div>

        <Container size="xl" className="relative z-10">
          <motion.div
            ref={heroRef}
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block mb-6"
            >
              <div className="glass-card px-4 py-2 rounded-full border border-[var(--color-electric-blue)]/30">
                <span className="text-sm font-semibold text-[var(--color-electric-blue)]">
                  Research & Insights
                </span>
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              The Lumen8 <span className="text-gradient">Blog</span>
            </h1>
            <p className="text-xl text-[var(--color-light-gray)] leading-relaxed">
              Exploring the science of learning, adaptive education technology, and evidence-based teaching strategies.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Link to={`/blog/${blogPosts[0].slug}`} className="block group">
              <div className="card-premium p-8 md:p-12 hover-glow transition-all duration-300">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-electric-blue)]/20 text-[var(--color-electric-blue)]">
                        Featured
                      </span>
                      <span className="text-sm text-[var(--color-medium-gray)]">{blogPosts[0].category}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 group-hover:text-[var(--color-electric-blue)] transition-colors">
                      {blogPosts[0].title}
                    </h2>
                    <p className="text-[var(--color-light-gray)] text-lg mb-6">
                      {blogPosts[0].excerpt}
                    </p>
                    <div className="flex items-center gap-6 text-sm text-[var(--color-medium-gray)]">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{blogPosts[0].date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-4 h-4" />
                        <span>{blogPosts[0].readTime}</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    {renderPreview(blogPosts[0].previewType, 'featured')}
                    <div className="absolute -top-4 -right-4 w-32 h-32 bg-[var(--color-electric-blue)] rounded-full filter blur-3xl opacity-20"></div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </Container>
      </section>

      {/* All Posts Grid */}
      <section className="py-16 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white">All Articles</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={`/blog/${post.slug}`} className="block group h-full">
                  <div className="card-premium p-6 h-full hover-glow transition-all duration-300 hover:scale-[1.02]">
                    <div className="mb-6">
                      {renderPreview(post.previewType, 'card')}
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-white/10 text-[var(--color-light-gray)]">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[var(--color-electric-blue)] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-[var(--color-light-gray)] text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-[var(--color-medium-gray)]">
                      <div className="flex items-center gap-4">
                        <span>{post.date}</span>
                        <span>{post.readTime}</span>
                      </div>
                      <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              See the Research <span className="text-gradient">in Action</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              Lumen8 puts these evidence-based learning principles to work. Experience adaptive learning that's grounded in science.
            </p>
            <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
              Request a Demo
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </motion.div>
        </Container>
      </section>
    </>
  );
};

export default Blog;
